import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { adminStaff, roleLabels, roleColors, roleDescriptions, statusLabels, defaultRolePermissions } from '@/mocks/adminStaff';
import type { StaffRole, StaffStatus, StaffMember } from '@/mocks/adminStaff';
import StaffTable from './components/StaffTable';
import StaffCard from './components/StaffCard';
import StaffFormModal from './components/StaffFormModal';
import type { StaffFormData } from './components/StaffFormModal';

const allRoles: Array<StaffRole | 'todos'> = ['todos', 'administrador', 'locutor', 'moderador', 'marketing', 'ventas', 'invitado'];
const allStatuses: Array<StaffStatus | 'todos'> = ['todos', 'active', 'invited', 'suspended', 'inactive'];

const roleIcons: Record<StaffRole, string> = {
  administrador: 'ri-shield-star-line',
  locutor: 'ri-mic-line',
  moderador: 'ri-chat-check-line',
  marketing: 'ri-megaphone-line',
  ventas: 'ri-money-dollar-circle-line',
  invitado: 'ri-user-received-line',
};

export default function AdminStaff() {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<StaffRole | 'todos'>('todos');
  const [filterStatus, setFilterStatus] = useState<StaffStatus | 'todos'>('todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'invite' | 'edit'>('invite');
  const [editingMember, setEditingMember] = useState<StaffMember | null>(null);

  const loadStaff = useCallback(() => {
    setLoading(true);
    setError(null);
    setTimeout(() => {
      try {
        setStaff([...adminStaff]);
        setLoading(false);
      } catch {
        setError('No se pudo cargar el equipo. Verificá tu conexión e intentá de nuevo.');
        setLoading(false);
      }
    }, 700);
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const filteredStaff = useMemo(() => {
    return staff.filter((m) => {
      const matchSearch =
        !search ||
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.email.toLowerCase().includes(search.toLowerCase()) ||
        m.bio.toLowerCase().includes(search.toLowerCase());
      const matchRole = filterRole === 'todos' || m.role === filterRole;
      const matchStatus = filterStatus === 'todos' || m.status === filterStatus;
      return matchSearch && matchRole && matchStatus;
    });
  }, [staff, search, filterRole, filterStatus]);

  // ---- HANDLERS ----

  const handleInvite = () => {
    setEditingMember(null);
    setModalMode('invite');
    setModalOpen(true);
  };

  const handleEdit = (member: StaffMember) => {
    setEditingMember(member);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleSave = async (data: StaffFormData) => {
    if (modalMode === 'edit' && editingMember) {
      setStaff((prev) =>
        prev.map((m) =>
          m.id === editingMember.id
            ? {
                ...m,
                name: data.name,
                role: data.role,
                email: data.email,
                bio: data.bio,
                photoUrl: data.photoUrl || m.photoUrl,
                socialLinks: {
                  instagram: data.instagram || undefined,
                  twitter: data.twitter || undefined,
                  youtube: data.youtube || undefined,
                  tiktok: data.tiktok || undefined,
                  website: data.website || undefined,
                },
                assignedPrograms: data.assignedPrograms
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
                permissions: [...data.permissions],
              }
            : m,
        ),
      );
      await SwalAlert.toast('Miembro actualizado correctamente.', 'success');
    } else {
      const newMember: StaffMember = {
        id: Math.max(...staff.map((m) => m.id), 0) + 1,
        name: data.name,
        role: data.role,
        email: data.email,
        bio: data.bio,
        photoUrl: data.photoUrl || 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20radio%20staff%20member%2C%20friendly%20face%2C%20studio%20lighting%2C%20clean%20background%2C%20modern%20editorial%20headshot&width=160&height=160&seq=staff-new-member-v2&orientation=squarish',
        socialLinks: {
          instagram: data.instagram || undefined,
          twitter: data.twitter || undefined,
          youtube: data.youtube || undefined,
          tiktok: data.tiktok || undefined,
          website: data.website || undefined,
        },
        assignedPrograms: data.assignedPrograms
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        permissions: [...data.permissions],
        joinedAt: new Date().toISOString(),
        status: 'invited',
        invitedAt: new Date().toISOString(),
      };
      setStaff((prev) => [newMember, ...prev]);
      await SwalAlert.toast(`Invitación enviada a ${data.email}.`, 'success');
    }
    setModalOpen(false);
    setEditingMember(null);
  };

  const handleDelete = async (member: StaffMember) => {
    const result = await SwalAlert.confirm(
      '¿Eliminar acceso?',
      `Esta acción es permanente. Se eliminará el acceso de "${member.name}" al sistema.${member.status === 'invited' ? ' La invitación pendiente también será cancelada.' : ''}`,
    );
    if (result.isConfirmed) {
      setStaff((prev) => prev.filter((m) => m.id !== member.id));
      await SwalAlert.toast('Acceso eliminado permanentemente.', 'info');
    }
  };

  const handleSuspend = async (member: StaffMember) => {
    const result = await SwalAlert.confirm(
      '¿Suspender acceso?',
      `Se suspenderá el acceso de "${member.name}". No podrá ingresar al sistema hasta que sea reactivado.`,
      'Sí, suspender',
      'Cancelar',
    );
    if (result.isConfirmed) {
      const { value: reason } = await SwalAlert.prompt(
        'Motivo de suspensión',
        'Describí brevemente el motivo (quedará registrado):',
        'Inactividad detectada',
      );
      setStaff((prev) =>
        prev.map((m) =>
          m.id === member.id
            ? {
                ...m,
                status: 'suspended',
                suspendedAt: new Date().toISOString(),
                suspensionReason: reason || 'Sin motivo especificado',
              }
            : m,
        ),
      );
      await SwalAlert.toast(`Acceso suspendido para ${member.name}.`, 'warning');
    }
  };

  const handleReactivate = async (member: StaffMember) => {
    const actionLabel = member.status === 'suspended' ? 'reactivar' : 'activar';
    const result = await SwalAlert.confirm(
      `¿${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} acceso?`,
      `Se ${actionLabel === 'reactivar' ? 'reactivará' : 'activará'} el acceso de "${member.name}".${member.status === 'suspended' ? ' Se eliminará el registro de suspensión.' : ''} Podrá ingresar al sistema normalmente.`,
    );
    if (result.isConfirmed) {
      setStaff((prev) =>
        prev.map((m) =>
          m.id === member.id
            ? {
                ...m,
                status: 'active',
                suspendedAt: undefined,
                suspensionReason: undefined,
              }
            : m,
        ),
      );
      await SwalAlert.toast(`Acceso ${actionLabel === 'reactivar' ? 'reactivado' : 'activado'} para ${member.name}.`, 'success');
    }
  };

  const handleResendInvite = async (member: StaffMember) => {
    const result = await SwalAlert.confirm(
      '¿Reenviar invitación?',
      `Se enviará nuevamente la invitación a "${member.email}". El link anterior dejará de funcionar.`,
    );
    if (result.isConfirmed) {
      setStaff((prev) =>
        prev.map((m) =>
          m.id === member.id
            ? { ...m, invitedAt: new Date().toISOString() }
            : m,
        ),
      );
      await SwalAlert.toast(`Invitación reenviada a ${member.email}.`, 'success');
    }
  };

  // ---- COUNTS ----

  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: staff.length };
    allRoles.forEach((r) => {
      if (r !== 'todos') counts[r] = staff.filter((m) => m.role === r).length;
    });
    return counts;
  }, [staff]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: staff.length };
    allStatuses.forEach((s) => {
      if (s !== 'todos') counts[s] = staff.filter((m) => m.status === s).length;
    });
    return counts;
  }, [staff]);

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-team-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Staff</h1>
              <p className="text-foreground-400 text-[9px]">{staff.length} miembros</p>
            </div>
          </div>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Staff & Permisos</h2>
              <p className="text-foreground-400 text-xs">
                Gestioná tu equipo.{' '}
                {staff.length} miembros · {statusCounts.active || 0} activos · {statusCounts.invited || 0} invitados · {statusCounts.suspended || 0} suspendidos
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
              <button
                onClick={handleInvite}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-user-add-line text-base"></i>
                Invitar usuario
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* Role stats cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {allRoles.map((role) => {
                const count = roleCounts[role] || 0;
                const isActive = filterRole === role;
                if (role === 'todos') {
                  return (
                    <button
                      key="todos"
                      onClick={() => setFilterRole('todos')}
                      className={`flex flex-col items-start gap-1 p-3 rounded-2xl border transition-all cursor-pointer ${
                        isActive
                          ? 'bg-foreground-900 text-background-50 border-foreground-900'
                          : 'bg-background-50 text-foreground-700 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      <i className={`ri-team-line ${isActive ? 'text-background-50' : 'text-foreground-400'} text-lg`}></i>
                      <span className={`text-xl font-bold ${isActive ? 'text-background-50' : 'text-foreground-900'}`}>{count}</span>
                    </button>
                  );
                }
                const r = role as StaffRole;
                return (
                  <button
                    key={r}
                    onClick={() => setFilterRole(isActive ? 'todos' : r)}
                    className={`flex flex-col items-start gap-1.5 p-3 rounded-2xl border transition-all cursor-pointer ${
                      isActive
                        ? 'bg-foreground-900 text-background-50 border-foreground-900'
                        : 'bg-background-50 text-foreground-700 border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${isActive ? 'bg-background-50/20' : 'bg-background-100'}`}>
                        <i className={`${roleIcons[r]} text-xs ${isActive ? 'text-background-50' : 'text-foreground-500'}`}></i>
                      </div>
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold ${roleColors[r]}`}>
                        {roleLabels[r]}
                      </span>
                    </div>
                    <span className={`text-xl font-bold ${isActive ? 'text-background-50' : 'text-foreground-900'}`}>{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Role descriptions legend */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-information-line text-foreground-400 text-sm"></i>
                <span className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Roles disponibles</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {(['administrador', 'locutor', 'moderador', 'marketing', 'ventas', 'invitado'] as StaffRole[]).map((r) => (
                  <div key={r} className="flex items-start gap-2.5 p-2.5 rounded-xl bg-background-100/70 hover:bg-background-100 transition-colors">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${r === 'administrador' ? 'bg-amber-100' : r === 'locutor' ? 'bg-accent-100' : r === 'moderador' ? 'bg-sky-100' : r === 'marketing' ? 'bg-rose-100' : r === 'ventas' ? 'bg-emerald-100' : 'bg-violet-100'}`}>
                      <i className={`${roleIcons[r]} text-xs ${r === 'administrador' ? 'text-amber-700' : r === 'locutor' ? 'text-accent-700' : r === 'moderador' ? 'text-sky-700' : r === 'marketing' ? 'text-rose-700' : r === 'ventas' ? 'text-emerald-700' : 'text-violet-700'}`}></i>
                    </div>
                    <div className="min-w-0">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold mb-0.5 ${roleColors[r]}`}>{roleLabels[r]}</span>
                      <p className="text-[10px] text-foreground-500 leading-relaxed">{roleDescriptions[r]}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Filters bar */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-4 space-y-3">
              {/* Search */}
              <div className="relative">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre, email o biografía..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                />
              </div>

              {/* Status filter chips */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Estado:</span>
                <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1">
                  {allStatuses.map((s) => {
                    const count = statusCounts[s] || 0;
                    const isActive = filterStatus === s;
                    const labels: Record<string, string> = {
                      todos: 'Todos',
                      active: 'Activos',
                      invited: 'Invitados',
                      suspended: 'Suspendidos',
                      inactive: 'Inactivos',
                    };
                    return (
                      <button
                        key={s}
                        onClick={() => setFilterStatus(isActive ? 'todos' : (s as StaffStatus))}
                        className={`px-3.5 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap ${
                          isActive
                            ? 'bg-background-50 text-foreground-900 shadow-sm'
                            : 'text-foreground-400 hover:text-foreground-600'
                        }`}
                      >
                        {labels[s] || s} ({count})
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={handleInvite}
                  className="lg:hidden ml-auto flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-user-add-line text-base"></i>
                  Invitar
                </button>
              </div>
            </div>

            {/* Results count */}
            <div className="flex items-center gap-2">
              <p className="text-xs text-foreground-400">
                Mostrando {filteredStaff.length} de {staff.length} miembros
              </p>
              {(filterRole !== 'todos' || filterStatus !== 'todos' || search) && (
                <button
                  onClick={() => {
                    setFilterRole('todos');
                    setFilterStatus('todos');
                    setSearch('');
                  }}
                  className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                >
                  Limpiar filtros
                </button>
              )}
            </div>

            {/* LOADING STATE */}
            {loading && (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="hidden lg:block bg-background-50 rounded-2xl border border-background-200 p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-full bg-background-200 shrink-0"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 rounded-md bg-background-200"></div>
                        <div className="h-3 w-48 rounded-md bg-background-200"></div>
                      </div>
                      <div className="h-6 w-20 rounded-lg bg-background-200"></div>
                      <div className="h-6 w-16 rounded-lg bg-background-200"></div>
                      <div className="flex items-center gap-1">
                        <div className="w-8 h-8 rounded-lg bg-background-200"></div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="lg:hidden space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-4 animate-pulse">
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 rounded-full bg-background-200 shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-28 rounded-md bg-background-200"></div>
                          <div className="h-3 w-20 rounded-md bg-background-200"></div>
                          <div className="h-3 w-full rounded-md bg-background-200"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ERROR STATE */}
            {!loading && error && (
              <div className="text-center py-16 bg-background-50 rounded-2xl border border-background-200">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                  <i className="ri-error-warning-line text-red-500 text-xl"></i>
                </div>
                <h3 className="text-foreground-800 font-bold text-sm mb-1">Error al cargar el equipo</h3>
                <p className="text-foreground-400 text-xs max-w-xs mx-auto leading-relaxed">{error}</p>
                <button
                  onClick={loadStaff}
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Reintentar
                </button>
              </div>
            )}

            {/* CONTENT */}
            {!loading && !error && (
              <>
                {/* Desktop Table */}
                <StaffTable
                  staff={filteredStaff}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onSuspend={handleSuspend}
                  onReactivate={handleReactivate}
                  onResendInvite={handleResendInvite}
                />

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3">
                  {filteredStaff.map((member) => (
                    <StaffCard
                      key={member.id}
                      member={member}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onSuspend={handleSuspend}
                      onReactivate={handleReactivate}
                      onResendInvite={handleResendInvite}
                    />
                  ))}
                  {filteredStaff.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-3">
                        <i className="ri-user-search-line text-foreground-400 text-xl"></i>
                      </div>
                      <p className="text-sm font-semibold text-foreground-600">Sin resultados</p>
                      <p className="text-xs text-foreground-400 mt-1">
                        No se encontraron miembros con los filtros actuales.
                      </p>
                    </div>
                  )}
                </div>

                {/* Info banner */}
                {filteredStaff.length > 0 && (
                  <div className="bg-background-50 rounded-2xl border border-background-200 p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-lightbulb-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-foreground-900 mb-1">Acciones rápidas del módulo</h4>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        <strong>Invitar</strong> — enviá un email con acceso temporal. <strong>Suspender</strong> — bloqueá el acceso sin eliminar al miembro, ideal para ausencias o incidencias. <strong>Eliminar</strong> — borrado permanente. Cada rol asigna automáticamente los permisos recomendados; podés personalizarlos desde Editar.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>



      {/* Modal */}
      <StaffFormModal
        open={modalOpen}
        member={editingMember}
        mode={modalMode}
        onClose={() => {
          setModalOpen(false);
          setEditingMember(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
}